import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsPencil, BsPersonPlus, BsSearch, BsTrash, BsXLg } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import { themeExpert } from '../../theme/expert';

const PAR_PAGE = 20;

const GENRES = ['Monsieur', 'Madame', 'Autre'];

const PAYS = [
  { code: 'BELGIQUE', libelle: 'Belgique' },
  { code: 'LUXEMBOURG', libelle: 'Luxembourg' },
];

const VIDE = {
  type: 'PERSONNE', genre: '', name: '', firstname: '', name_company: '',
  entreprise_number: '', vat: '', email: '', phone: '', street: '',
  number: '', letter_box: '', city: '', postcode: '', country: '',
};

/** Le carnet d'adresses de l'organisation, repris d'un dossier à l'autre. */
function PersonManager() {
  const [personnes, setPersonnes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [recherche, setRecherche] = useState('');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/persons/', {
        params: { page, ...(recherche ? { search: recherche } : {}) },
      });
      if (data.results !== undefined) {
        setPersonnes(data.results);
        setTotal(data.count);
      } else {
        setPersonnes(data);
        setTotal(data.length);
      }
      setErreur('');
    } catch {
      setErreur("Le carnet n'a pas pu être chargé.");
    } finally {
      setChargement(false);
    }
  }, [page, recherche]);

  useEffect(() => {
    const minuterie = setTimeout(charger, 300);
    return () => clearTimeout(minuterie);
  }, [charger]);

  const poser = (champ) => (e) =>
    setFiche((etat) => ({ ...etat, [champ]: e.target.value }));

  const entreprise = fiche?.type === 'ENTREPRISE';

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      if (fiche.id) {
        await axiosInstance.put(`/persons/${fiche.id}/`, fiche);
      } else {
        await axiosInstance.post('/persons/', fiche);
      }
      setFiche(null);
      setMessage('Fiche enregistrée');
      charger();
    } catch {
      setErreur("La fiche n'a pas pu être enregistrée.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/persons/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Fiche supprimée');
      charger();
    } catch {
      setErreur("La fiche n'a pas pu être supprimée.");
    } finally {
      setOccupe(false);
    }
  };

  const nom = (p) => (p.type === 'ENTREPRISE'
    ? p.name_company || '—'
    : [p.name, p.firstname].filter(Boolean).join(' ') || '—');

  const adresse = (p) => [
    [p.number, p.street].filter(Boolean).join(' '),
    [p.postcode, p.city].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');

  const pages = Math.max(1, Math.ceil(total / PAR_PAGE));

  return (
    <>
      <Fiche
        titre="Carnet d'adresses"
        explication="Les personnes et entreprises qu'on retrouve d'un dossier à l'autre. Reprises d'un clic dans les intervenants."
        actions={(
          <Button size="small" variant="contained" startIcon={<BsPersonPlus />}
                  onClick={() => setFiche({ ...VIDE })}>
            Nouvelle fiche
          </Button>
        )}
        sx={{ p: 0 }}
      >
        <Box sx={{ px: 2, pt: 2 }}>
          <TextField
            value={recherche}
            onChange={(e) => { setRecherche(e.target.value); setPage(1); }}
            placeholder="Chercher un nom, une société…"
            sx={{ maxWidth: 380 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><BsSearch /></InputAdornment>
              ),
              endAdornment: recherche ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Effacer"
                              onClick={() => { setRecherche(''); setPage(1); }}>
                    <BsXLg size={12} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {erreur && <Alert severity="error" sx={{ m: 2 }}
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && personnes.length === 0 && (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[...Array(4)].map((_, i) => <Skeleton key={i} height={36} />)}
          </Stack>
        )}

        {!chargement && personnes.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}>
            {recherche ? 'Aucune fiche ne correspond.' : 'Le carnet est vide.'}
          </Typography>
        )}

        {personnes.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Coordonnées</TableCell>
                  <TableCell>Adresse</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {personnes.map((personne) => (
                  <TableRow key={personne.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center"
                             flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {nom(personne)}
                        </Typography>
                        {personne.type === 'ENTREPRISE' && (
                          <Chip size="small" variant="outlined"
                                label="entreprise" />
                        )}
                      </Stack>
                      {personne.entreprise_number && (
                        <Typography variant="caption" color="text.secondary">
                          {personne.entreprise_number}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {personne.email && (
                        <Box component="a" href={`mailto:${personne.email}`}
                             sx={{ color: 'primary.main', display: 'block',
                                   fontSize: '0.85rem' }}>
                          {personne.email}
                        </Box>
                      )}
                      {personne.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {personne.phone}
                        </Typography>
                      )}
                      {!personne.email && !personne.phone && (
                        <Box component="span"
                             sx={{ color: 'text.disabled' }}>—</Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {adresse(personne) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small"
                                    onClick={() => setFiche({
                                      ...VIDE, ...personne })}>
                          <BsPencil />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(personne)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {pages > 1 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                 alignItems="center" justifyContent="space-between"
                 sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {(page - 1) * PAR_PAGE + 1}–{Math.min(page * PAR_PAGE, total)}
              {' '}sur {total}
            </Typography>
            <Pagination count={pages} page={page}
                        onChange={(_, v) => setPage(v)} />
          </Stack>
        )}
      </Fiche>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="md">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? 'Modifier la fiche' : 'Nouvelle fiche'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <ToggleButtonGroup size="small" exclusive
                                   value={fiche?.type || 'PERSONNE'}
                                   onChange={(_, v) => v && setFiche(
                                     (s) => ({ ...s, type: v }))}>
                  <ToggleButton value="PERSONNE">Personne</ToggleButton>
                  <ToggleButton value="ENTREPRISE">Entreprise</ToggleButton>
                </ToggleButtonGroup>

                {/* Les champs d'entreprise étaient toujours affichés, même
                    pour une personne physique. */}
                {entreprise ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Raison sociale"
                               value={fiche?.name_company || ''}
                               onChange={poser('name_company')} required
                               autoFocus />
                    <TextField label="N° d'entreprise"
                               value={fiche?.entreprise_number || ''}
                               onChange={poser('entreprise_number')} />
                    <TextField label="TVA" value={fiche?.vat || ''}
                               onChange={poser('vat')} />
                  </Stack>
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField select label="Genre" value={fiche?.genre || ''}
                               onChange={poser('genre')}
                               sx={{ minWidth: 140 }}>
                      <MenuItem value=""><em>Aucun</em></MenuItem>
                      {GENRES.map((g) => (
                        <MenuItem key={g} value={g}>{g}</MenuItem>
                      ))}
                    </TextField>
                    <TextField label="Nom" value={fiche?.name || ''}
                               onChange={poser('name')} required autoFocus />
                    <TextField label="Prénom" value={fiche?.firstname || ''}
                               onChange={poser('firstname')} />
                  </Stack>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Email" type="email"
                             value={fiche?.email || ''}
                             onChange={poser('email')} />
                  <TextField label="Téléphone" value={fiche?.phone || ''}
                             onChange={poser('phone')} />
                </Stack>

                <Box>
                  <Typography variant="body2" color="text.secondary"
                              sx={{ mb: 1 }}>Adresse</Typography>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="Numéro" value={fiche?.number || ''}
                                 onChange={poser('number')}
                                 sx={{ maxWidth: { sm: 120 } }} />
                      <TextField label="Boîte" value={fiche?.letter_box || ''}
                                 onChange={poser('letter_box')}
                                 sx={{ maxWidth: { sm: 120 } }} />
                      <TextField label="Rue" value={fiche?.street || ''}
                                 onChange={poser('street')} />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="Code postal"
                                 value={fiche?.postcode || ''}
                                 onChange={poser('postcode')}
                                 sx={{ maxWidth: { sm: 160 } }} />
                      <TextField label="Ville" value={fiche?.city || ''}
                                 onChange={poser('city')} />
                      <TextField select label="Pays"
                                 value={fiche?.country || ''}
                                 onChange={poser('country')}
                                 sx={{ minWidth: 180 }}>
                        <MenuItem value=""><em>Aucun</em></MenuItem>
                        {PAYS.map(({ code, libelle }) => (
                          <MenuItem key={code} value={code}>{libelle}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || (entreprise
                        ? !fiche?.name_company?.trim()
                        : !fiche?.name?.trim())}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer cette fiche ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer && nom(aSupprimer)} » sortira du carnet. Les
              intervenants déjà créés dans des dossiers à partir d'elle ne
              bougent pas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default PersonManager;
